import { useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'

import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'

import { useSendNetworkInvitationMutation } from '@/core/services/network'

import { getApiErrorMessage } from '@/utils/errors'

import InviteByEmailDialog from './InviteByEmailDialog'
import InviteStaffsDialog from './InviteStaffsDialog'

type InviteButtonMenuProps = {
  networkId: string
}

export default function InviteButtonMenu(props: InviteButtonMenuProps) {
  const { networkId } = props
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [openInviteByEmailDialog, setOpenInviteByEmailDialog] = useState(false)
  const [openInviteStaffsDialog, setOpenInviteStaffsDialog] = useState(false)

  const [sendNetworkInvitation, { isLoading: isSending }] = useSendNetworkInvitationMutation()

  return (
    <>
      <PopupState variant="popover" popupId="network-invite-button">
        {(popupState) => (
          <>
            <Button {...bindTrigger(popupState)} color="primary" startIcon={<AddRoundedIcon />} variant="contained" data-testid="network-invite-trigger">
              {t('common:button.invite.label')}
            </Button>
            <Menu
              {...bindMenu(popupState)}
              elevation={0}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem
                onClick={() => {
                  setOpenInviteStaffsDialog(true)
                  popupState.close()
                }}
              >
                <ListItemIcon>
                  <Groups2OutlinedIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('page.network.inviteButtonMenu.item.sameEstablishment.primary')}
                  secondary={t('page.network.inviteButtonMenu.item.sameEstablishment.secondary')}
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOpenInviteByEmailDialog(true)
                  popupState.close()
                }}
                data-testid="network-invite-by-email"
              >
                <ListItemIcon>
                  <MailOutlineRoundedIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('page.network.inviteButtonMenu.item.byEmail.primary')}
                  secondary={t('page.network.inviteButtonMenu.item.byEmail.secondary')}
                />
              </MenuItem>
            </Menu>
          </>
        )}
      </PopupState>
      <InviteByEmailDialog
        open={openInviteByEmailDialog}
        onClose={() => setOpenInviteByEmailDialog(false)}
        onSend={async (emails) => {
          try {
            await sendNetworkInvitation({ networkId, emails }).unwrap()
            setOpenInviteByEmailDialog(false)
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.network.invite.error')), { variant: 'error' })
          }
        }}
      />
      <InviteStaffsDialog
        isSending={isSending}
        networkId={networkId}
        open={openInviteStaffsDialog}
        onClose={() => setOpenInviteStaffsDialog(false)}
        onSend={async (staffs) => {
          try {
            await sendNetworkInvitation({ networkId, staffs }).unwrap()
            setOpenInviteStaffsDialog(false)
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.network.invite.error')), { variant: 'error' })
          }
        }}
      />
    </>
  )
}
